import { AbsoluteCenter, Box, Divider, Flex, Input } from "@chakra-ui/react";
import { Typography, useToast } from "@reconvert/react-ui-component";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAppState } from "../../app/slice/appSlice";
import useSlots from "../../hooks/useSlots";
import { Platform, Slot, SlotStatus } from "../../models";
import useUnsavedBar, { PreventActionType, PreventActionTypeObject } from "../../hooks/useUnsavedBar";
import { updateSlot } from "../../app/slice/slotSlice";
import { MAX_CUSTOM_SLOT, PageType, PreMadeTemplateSlotMapping, PreMadeTemplateType } from "@reconvert/reconvert-utils";
import { APP_PLATFORM } from "../../config";
import { DEFAULT_SLOT_STORE_TEMPLATE } from "../../config";
import SlotWarningBanner from "../SlotVariant/SlotWarningBanner";
import { selectBannerState } from "../../app/slice/bannerSlice";
import SlotPublishWarning from "../SlotVariant/slotPublishWarning";

interface SlotStatusType {
  slotNo: number;
  slotActiveOnPage: boolean;
  location: string;
}

const SlotHeader = ({ slot }: { slot: Slot }) => {
  const { t } = useTranslation();
  const { activePage, platformStoreId } = useAppSelector(selectAppState);
  const { error } = useToast();
  const { handleSlotNameUpdate, isPreMadeTemplateSlot } = useSlots();
  const [isEdit, setIsEdit] = useState(false);
  const [name, setName] = useState(slot?.name);
  const [preserveName, setPreserveName] = useState(slot?.name);
  const dispatch = useAppDispatch();
  const { pushUnsavedChanges, preventAction } = useUnsavedBar();
  const { slotStatuses } = useAppSelector(selectBannerState);

  const isTemplateSlot = isPreMadeTemplateSlot(slot.slotNo, slot.type);
  const isSlotNameEditable = !isTemplateSlot || slot.type === PreMadeTemplateType.QUANTITY_BREAKS;

  const isSlotPublishWarningVisible = useMemo(() => {
    return activePage === PageType.PRE_PURCHASE_PRODUCT_PAGE && APP_PLATFORM === Platform.Wix && (slot.slotStatus === SlotStatus.NEVER_DETECTED || !slot.slotStatus);
  }, [activePage, slot?.slotStatus]);

  useEffect(() => {
    setName(t(slot?.name));
    setPreserveName(t(slot?.name));
  }, [slot?.name]);

  const toggleEdit = () => {
    preventAction(() => {
      if (!name) {
        setName(preserveName);
        setIsEdit(!isEdit);
        return error(t("Slot name can't be empty"));
      }

      if (isEdit) {
        pushUnsavedChanges({
          id: "slotNameChange",
          type: "SLOT",
          saveAction: () => handleSlotNameUpdate(slot.id, slot.name, name),
          discardAction: () => {
            setName(slot.name);
            dispatch(updateSlot({ id: slot.id, name: slot.name }));
          },
        });
      }

      setIsEdit(!isEdit);
    }, PreventActionTypeObject.INLINE_CHANGE as PreventActionType);
  };

  const handleNameChange = (e: any) => {
    if (e.target.value) {
      setPreserveName(e.target.value);
    }

    setName(e.target.value);
  };

  const getSlotName = useCallback(() => {
    if (platformStoreId === DEFAULT_SLOT_STORE_TEMPLATE) return `${t("Slot")} ${slot?.slotNo}`;

    switch (activePage) {
      case PageType.POST_PURCHASE_PAGE1:
        return t("1st page");
      case PageType.POST_PURCHASE_PAGE2:
        return t("2nd page");
      case PageType.PRE_PURCHASE_CART_DRAWER_PAGE:
        return t("Cart drawer");
      case PageType.PRE_PURCHASE_ADD_TO_CART_CLICK:
        return t("Add to cart click");
      case PageType.PRE_PURCHASE_CHECKOUT_CLICK:
        return t("Checkout click");
      default:
        return isTemplateSlot
          ? Object.values(PreMadeTemplateSlotMapping)
              .find(a => a.slotNo === slot?.slotNo)
              ?.title()
          : `${t("Slot")} ${slot?.slotNo}`;
    }
  }, [platformStoreId, t, slot?.slotNo, activePage, isTemplateSlot]);

  const isSlotWarningBannerVisible = useMemo(() => {
    if (APP_PLATFORM !== Platform.Shopify || !Array.isArray(slotStatuses) || slotStatuses.length === 0) {
      return false;
    }

    return slotStatuses.some(
      (status: SlotStatusType) =>
        !status?.slotActiveOnPage && status?.location === activePage && status?.slotNo === slot?.slotNo,
    );
  }, [activePage, slot?.slotNo, slotStatuses]);

  const isNameTranslated =
    activePage === PageType.POST_PURCHASE_PAGE1 ||
    activePage === PageType.POST_PURCHASE_PAGE2 ||
    activePage === PageType.PRE_PURCHASE_CART_DRAWER_PAGE
      ? true
      : false;

  return (
    <Flex mt={slot.slotNo === 1 ? 4 : "24px"} direction={"column"} sx={{ width: "100%", padding: "24px 0" }}>
      <Box marginBottom={"24px"} position="relative">
        <Divider color="divider.2" p={0} borderTop="2px solid divider.2" />
        <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" bg={"#fbfcfe"} px="2" maxW="90%">
          <Typography textAlign={"center"} variant="h2-r-medium" fontWeight={500}>
            {getSlotName()}
          </Typography>
          {isSlotNameEditable && (
            <>
              {isEdit ? (
                <Input
                  htmlSize={name.length}
                  width="auto"
                  onChange={handleNameChange}
                  value={isNameTranslated ? t(name) : name}
                  size={"xs"}
                  onBlur={toggleEdit}
                  textAlign={"center"}
                  sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "ellipsis", maxW: "100%" }}
                />
              ) : (
                <Typography onClick={toggleEdit} textAlign={"center"} variant="subheading2" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "ellipsis"}}>
                  {name}          
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
      {isSlotWarningBannerVisible && <SlotWarningBanner slot={slot} />}
      {isSlotPublishWarningVisible && <SlotPublishWarning slot={slot} />}
    </Flex>
  );
};

export default SlotHeader;
